import { Fragment, useState, type ReactNode } from 'react';
import {
  ActionIcon,
  Alert,
  FileButton,
  Badge,
  Button,
  Center,
  Checkbox,
  Group,
  Loader,
  Menu,
  Modal,
  NumberInput,
  Paper,
  SegmentedControl,
  Select,
  Stack,
  Switch,
  Text,
  TextInput,
  Textarea,
  ThemeIcon,
  Tooltip,
  Box,
} from '@mantine/core';
import {
  IconArchive,
  IconBuildingStore,
  IconCertificate,
  IconCloud,
  IconDeviceMobile,
  IconFileText,
  IconLicense,
  IconPaperclip,
  IconServer,
  IconTool,
  IconUserCheck,
  IconWifi,
  IconWorldWww,
  IconArchiveOff,
  IconCalendarDue,
  IconDotsVertical,
  IconDownload,
  IconHistory,
  IconSparkles,
  IconEye,
  IconInfoCircle,
  IconEyeOff,
  IconPencil,
  IconPlus,
  IconSearch,
  IconHourglass,
  IconShieldCheck,
  IconTrash,
} from '@tabler/icons-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { api } from '../../lib/api';
import { downloadCsv, fileSlug } from '../../lib/csvExport';
import { contractsCsvRows, type ContractCsvMode } from './contractsCsv';
import type { TechnicianDeadline, CustomerDeadlineInput } from '../../lib/api/domains/customerDeadlines';
import {
  DEADLINE_CATEGORY_LABELS,
  noticeLabel,
  DEADLINE_CATEGORY_ORDER,
  DEADLINE_OWNERSHIP_LABELS,
  DEADLINE_REQUEST_KIND_LABELS,
  DEADLINE_STATUS_META,
  formatDaysLeft,
  formatIsoDateDe,
  needsCustomerAction,
  type CustomerDeadlineCategory,
  type CustomerDeadlineOwnership,
  type CustomerDeadlineTermType,
} from '../../lib/customerDeadlines';
import { InfoEmpty, LabelWithHint } from './CustomerInfoParts';
import { DeadlineChangeLogModal } from './DeadlineChangeLogModal';
import {
  DeadlineProofsField,
  fileToBase64,
  imagesFromClipboard,
  toPendingProofs,
  type PendingProof,
} from './DeadlineProofsField';
import { ProofRedactModal, type RedactTarget } from './ProofRedactModal';
import { useCustomerDeadlines } from './customerInfoQueries';
import classes from './CustomerInfo.module.css';

/** Symbol je Kategorie — die Farbe davor zeigt den Inhaber (orange FixByte, blau Kunde). */
const CATEGORY_ICONS: Record<CustomerDeadlineCategory, typeof IconLicense> = {
  LICENSE: IconLicense,
  SERVER: IconServer,
  HOSTING: IconCloud,
  DOMAIN: IconWorldWww,
  CERTIFICATE: IconCertificate,
  CONTRACT: IconFileText,
  SUPPORT: IconTool,
  MOBILE: IconDeviceMobile,
  INTERNET: IconWifi,
  OTHER: IconCalendarDue,
};

function OwnerFilterLabel({ color, text }: { color: string; text: string }) {
  return (
    <Group gap={5} wrap="nowrap" justify="center">
      <Box w={8} h={8} style={{ borderRadius: 4, backgroundColor: `var(--mantine-color-${color}-6)`, flexShrink: 0 }} />
      <span>{text}</span>
    </Group>
  );
}

interface FormState {
  category: CustomerDeadlineCategory;
  ownership: CustomerDeadlineOwnership;
  termType: CustomerDeadlineTermType;
  name: string;
  provider: string;
  reference: string;
  quantity: number | '';
  startDate: string;
  endDate: string;
  autoRenew: boolean;
  renewalMonths: number | '';
  noticeDays: number | '';
  customerNote: string;
  internalNote: string;
  visibleInPortal: boolean;
}

const EMPTY_FORM: FormState = {
  category: 'LICENSE',
  ownership: 'FIXBYTE',
  termType: 'FIXED',
  name: '',
  provider: '',
  reference: '',
  quantity: '',
  startDate: '',
  endDate: '',
  autoRenew: false,
  renewalMonths: '',
  noticeDays: '',
  customerNote: '',
  internalNote: '',
  visibleInPortal: true,
};

function toForm(d: TechnicianDeadline): FormState {
  return {
    category: d.category,
    ownership: d.ownership,
    termType: d.termType,
    name: d.name,
    provider: d.provider ?? '',
    reference: d.reference ?? '',
    quantity: d.quantity ?? '',
    startDate: d.startDate ?? '',
    endDate: d.endDate ?? '',
    autoRenew: d.autoRenew,
    renewalMonths: d.renewalMonths ?? '',
    noticeDays: d.noticeDays ?? '',
    customerNote: d.customerNote ?? '',
    internalNote: d.internalNote ?? '',
    visibleInPortal: d.visibleInPortal,
  };
}

function toPayload(f: FormState): CustomerDeadlineInput & { name: string } {
  const service = f.termType === 'SERVICE_CONTRACT';
  return {
    category: f.category,
    // Managed Services bezieht der Kunde über FixByte — der Server prüft das ebenfalls.
    ownership: service ? 'FIXBYTE' : f.ownership,
    name: f.name.trim(),
    provider: f.provider.trim(),
    reference: f.reference.trim(),
    quantity: f.quantity === '' ? null : Number(f.quantity),
    startDate: f.startDate || null,
    termType: f.termType,
    // Datum nur bei eigener Laufzeit; bei „angefragt" folgt es, beim Servicevertrag gibt es keins.
    endDate: f.termType === 'FIXED' ? f.endDate : null,
    autoRenew: service ? false : f.autoRenew,
    renewalMonths: service || f.renewalMonths === '' ? null : Number(f.renewalMonths),
    noticeDays: service || f.noticeDays === '' ? null : Number(f.noticeDays),
    customerNote: f.customerNote,
    internalNote: f.internalNote,
    visibleInPortal: f.visibleInPortal,
  };
}

/**
 * Fristen-Register im Kunden-Flyout: Lizenzen, Server, Domains, Zertifikate, Verträge
 * die FixByte im eigenen Namen bereitstellt (FIXBYTE) oder für den Kunden verwaltet
 * (CUSTOMER_MANAGED). Wird im Kundenportal unter „Fristen" angezeigt.
 */
export function CustomerDeadlinesSection({
  weclappCustomerId,
  cwCompanyId,
  localCustomerId,
  fallback,
  withTitle = true,
}: {
  weclappCustomerId?: string;
  cwCompanyId?: number;
  localCustomerId?: string;
  /** Anzeige, wenn der Kunde lokal (noch) nicht existiert. */
  fallback?: ReactNode;
  /** Überschrift weglassen — z. B. im gleichnamigen Reiter der Kundeninfos. */
  withTitle?: boolean;
}) {
  const queryClient = useQueryClient();
  const { data, isLoading, queryKey } = useCustomerDeadlines({ localCustomerId, weclappCustomerId, cwCompanyId });

  const [search, setSearch] = useState('');
  const [ownerFilter, setOwnerFilter] = useState<'ALL' | CustomerDeadlineOwnership>('ALL');
  /** Eingefügte Belege, die beim Speichern hochgeladen werden */
  const [pendingProofs, setPendingProofs] = useState<PendingProof[]>([]);
  /** Bilder, die vor dem Ablegen verpixelt werden können — eins nach dem anderen */
  const [redactQueue, setRedactQueue] = useState<RedactTarget[]>([]);
  const [logOpen, setLogOpen] = useState(false);
  /** null = Protokoll des ganzen Kunden */
  const [logItem, setLogItem] = useState<{ id: string; name: string } | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [editItem, setEditItem] = useState<TechnicianDeadline | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [deleteItem, setDeleteItem] = useState<TechnicianDeadline | null>(null);

  // Prefill aus Dokument (Screenshot/PDF)
  const [prefillState, setPrefillState] = useState<'idle' | 'loading' | 'done' | 'error'>('idle');
  const [prefillNote, setPrefillNote] = useState<string | null>(null);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey });
    // Jede Änderung erzeugt einen Protokolleintrag — ein offenes Protokoll soll ihn zeigen.
    void queryClient.invalidateQueries({ queryKey: ['customer-deadline-changes'] });
  };
  const closeModal = () => {
    setAddOpen(false);
    setEditItem(null);
    pendingProofs.forEach((p) => URL.revokeObjectURL(p.previewUrl));
    setPendingProofs([]);
    setRedactQueue([]);
    setPrefillState('idle');
    setPrefillNote(null);
  };

  /**
   * Erkannte Werte ins Formular übernehmen. Bewusst nur dort, wo der Techniker
   * noch nichts eingetragen hat — bereits getippte Angaben bleiben stehen.
   */
  async function handlePrefillFile(file: File | null) {
    if (!file) return;
    setPrefillState('loading');
    setPrefillNote(null);
    try {
      const { prefill } = await api.prefillDeadlineFromDocument(file);
      let filled = 0;
      setForm((f) => {
        const next = { ...f };
        const setText = (key: 'name' | 'provider' | 'reference', value: string | null) => {
          if (value && !next[key].trim()) {
            next[key] = value;
            filled++;
          }
        };
        const setNum = (key: 'quantity' | 'renewalMonths' | 'noticeDays', value: number | null) => {
          if (value != null && next[key] === '') {
            next[key] = value;
            filled++;
          }
        };
        const setDate = (key: 'startDate' | 'endDate', value: string | null) => {
          if (value && !next[key]) {
            next[key] = value;
            filled++;
          }
        };
        if (prefill.category) {
          next.category = prefill.category;
          filled++;
        }
        setText('name', prefill.name);
        setText('provider', prefill.provider);
        setText('reference', prefill.reference);
        setNum('quantity', prefill.quantity);
        setDate('startDate', prefill.startDate);
        setDate('endDate', prefill.endDate);
        if (prefill.autoRenew != null && !next.autoRenew) {
          next.autoRenew = prefill.autoRenew;
          if (prefill.autoRenew) filled++;
        }
        setNum('renewalMonths', prefill.renewalMonths);
        setNum('noticeDays', prefill.noticeDays);
        return next;
      });
      setPrefillState('done');
      setPrefillNote(
        filled === 0
          ? 'Aus dem Dokument ließ sich nichts eindeutig übernehmen — bitte manuell ausfüllen.'
          : `${filled} ${filled === 1 ? 'Feld' : 'Felder'} vorbefüllt${prefill.note ? ` — ${prefill.note}` : ''}. Bitte prüfen.`
      );
    } catch (err) {
      setPrefillState('error');
      setPrefillNote(
        err instanceof Error ? err.message : 'Das Dokument konnte nicht ausgewertet werden.'
      );
    }
  }

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = toPayload(form);
      const saved = editItem
        ? await api.updateCustomerDeadline(editItem.id, payload)
        : await api.createCustomerDeadline(data!.customer.id, payload);
      // Belege erst nach dem Posten — beim Anlegen gibt es vorher keine ID, an der sie hängen.
      const failed: string[] = [];
      for (const item of pendingProofs) {
        try {
          await api.uploadDeadlineProof(saved.deadline.id, {
            base64: await fileToBase64(item.file),
            filename: item.file.name,
            visibleToCustomer: item.visibleToCustomer,
          });
        } catch (err) {
          failed.push(`${item.file.name}: ${err instanceof Error ? err.message : 'Upload fehlgeschlagen'}`);
        }
      }
      return { failed };
    },
    onSuccess: ({ failed }) => {
      invalidate();
      closeModal();
      if (failed.length > 0) {
        notifications.show({
          title: `Gespeichert — ${failed.length} ${failed.length === 1 ? 'Beleg' : 'Belege'} nicht hochgeladen`,
          message: failed.join('\n'),
          color: 'orange',
          autoClose: 12_000,
        });
      } else {
        notifications.show({ title: 'Gespeichert', message: '', color: 'green' });
      }
    },
    onError: (e: Error) => notifications.show({ title: 'Fehler', message: e.message, color: 'red' }),
  });

  const patchMutation = useMutation({
    mutationFn: ({ id, data: patch }: { id: string; data: CustomerDeadlineInput }) =>
      api.updateCustomerDeadline(id, patch),
    onSuccess: () => invalidate(),
    onError: (e: Error) => notifications.show({ title: 'Fehler', message: e.message, color: 'red' }),
  });

  const resolveMutation = useMutation({
    mutationFn: (id: string) => api.resolveCustomerDeadlineRequest(id),
    onSuccess: () => {
      invalidate();
      notifications.show({ title: 'Kundenanfrage als erledigt markiert', message: '', color: 'green' });
    },
    onError: (e: Error) => notifications.show({ title: 'Fehler', message: e.message, color: 'red' }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteCustomerDeadline(id),
    onSuccess: () => {
      invalidate();
      setDeleteItem(null);
      notifications.show({ title: 'Gelöscht', message: '', color: 'green' });
    },
    onError: (e: Error) => notifications.show({ title: 'Fehler', message: e.message, color: 'red' }),
  });

  // Kein lokaler Kunde → Fallback des Aufrufers (im Flyout: nichts, die Care-Section zeigt den Hinweis).
  if (!isLoading && !data) return <>{fallback ?? null}</>;

  const all = data?.deadlines ?? [];
  const active = all.filter((d) => d.isActive);
  const archived = all.filter((d) => !d.isActive);
  const urgent = active.filter((d) => d.status !== null && needsCustomerAction(d.status)).length;
  const openRequests = active.filter((d) => d.pendingRequest).length;
  const query = search.trim().toLowerCase();
  const confirmDelete = (d: TechnicianDeadline) => setDeleteItem(d);
  const base = showArchived ? all : active;
  const fixbyteCount = base.filter((d) => d.ownership === 'FIXBYTE').length;
  const customerCount = base.length - fixbyteCount;
  const list = base.filter((d) => ownerFilter === 'ALL' || d.ownership === ownerFilter).filter(
    (d) =>
      !query ||
      [d.name, d.provider, d.reference, DEADLINE_CATEGORY_LABELS[d.category]].some((v) =>
        v?.toLowerCase().includes(query),
      ),
  );

  const openAdd = () => {
    setForm(EMPTY_FORM);
    setAddOpen(true);
  };

  /**
   * CSV für Excel. Exportiert genau die Zeilen, die gerade in der Liste stehen —
   * Inhaber-Filter, Suche und Archiv-Schalter gelten also auch für die Datei.
   * Die Kundenfassung lässt interne Posten, Stückzahl und interne Notiz weg.
   */
  const exportCsv = (mode: ContractCsvMode) => {
    const customerName = data?.customer.name ?? 'Kunde';
    const rows = contractsCsvRows(list, customerName, mode);
    const count = rows.length - 2; // Kopfzeile + Spaltenüberschriften
    if (count === 0) {
      notifications.show({
        title: 'Nichts zu exportieren',
        message:
          mode === 'customer'
            ? 'Keine der sichtbaren Zeilen ist im Kundenportal freigegeben — nimm „Intern (alle Angaben)".'
            : 'Die aktuelle Auswahl enthält keine Einträge.',
        color: 'orange',
      });
      return;
    }
    // Lokales Datum, nicht UTC — sonst steht kurz nach Mitternacht der Vortag im Namen.
    const date = new Date().toLocaleDateString('sv-SE');
    const filename = `fixbyte-vertraege-lizenzen-${fileSlug(customerName)}${mode === 'internal' ? '-intern' : ''}-${date}.csv`;
    downloadCsv(filename, rows);
    notifications.show({
      title: 'CSV gespeichert',
      message: `${count} Posten · ${filename}`,
      color: 'teal',
    });
  };

  // Leerer Zustand mit dem nächsten sinnvollen Schritt statt nur einem Satz.
  const empty = query
    ? { text: 'Nichts passt zur Suche.', action: null }
    : ownerFilter !== 'ALL' && base.length > 0
      ? {
          text: 'Keine Einträge für diesen Inhaber.',
          action: (
            <Button size="xs" variant="default" onClick={() => setOwnerFilter('ALL')}>
              Alle zeigen
            </Button>
          ),
        }
    : archived.length > 0 && !showArchived
      ? {
          text: `Keine aktiven Einträge — ${archived.length} archiviert.`,
          action: (
            <Button size="xs" variant="default" onClick={() => setShowArchived(true)}>
              Archiv zeigen
            </Button>
          ),
        }
      : {
          text: 'Noch nichts hinterlegt. Beim Hinzufügen liest KIAN Laufzeit und Kündigungsfrist auch aus einem PDF oder Screenshot.',
          action: (
            <Button size="xs" variant="light" leftSection={<IconPlus size={14} />} onClick={openAdd}>
              Ersten Eintrag hinzufügen
            </Button>
          ),
        };

  return (
    <div>
      {/* Kopfleiste gehört zur Karte — frei darüber wirkte sie wie ein zweiter Seitenkopf. */}
      <Paper withBorder radius="md" style={{ overflow: 'hidden', backgroundColor: 'var(--mantine-color-default)' }}>
        <Group
          justify="space-between"
          gap="sm"
          wrap="wrap"
          px="md"
          py="sm"
          style={{ borderBottom: '1px solid var(--mantine-color-default-border)' }}
        >
          <Group gap={8} wrap="wrap">
            {withTitle ? (
              <Group gap={8} wrap="nowrap">
                <ThemeIcon size={26} radius="md" variant="light" color="teal">
                  <IconCalendarDue size={15} />
                </ThemeIcon>
                <Text fw={600} size="sm">
                  Verträge & Lizenzen
                </Text>
                <Badge size="sm" variant="default" radius="sm">
                  {active.length}
                </Badge>
              </Group>
            ) : (
              <Text size="sm" c="dimmed">
                {active.length} aktiv{archived.length > 0 ? ` · ${archived.length} archiviert` : ''}
              </Text>
            )}
            {urgent > 0 && (
              <Badge size="sm" color="orange" variant="light">
                {urgent} fällig
              </Badge>
            )}
            {openRequests > 0 && (
              <Badge size="sm" color="grape" variant="light">
                {openRequests} Kundenanfrage{openRequests === 1 ? '' : 'n'}
              </Badge>
            )}
          </Group>
          <Group gap="sm" wrap="wrap">
            {fixbyteCount > 0 && customerCount > 0 && (
              <SegmentedControl
                size="xs"
                value={ownerFilter}
                onChange={(v) => setOwnerFilter(v as 'ALL' | CustomerDeadlineOwnership)}
                data={[
                  { value: 'ALL', label: 'Alle' },
                  { value: 'FIXBYTE', label: <OwnerFilterLabel color="orange" text={`FixByte ${fixbyteCount}`} /> },
                  { value: 'CUSTOMER_MANAGED', label: <OwnerFilterLabel color="blue" text={`Kunde ${customerCount}`} /> },
                ]}
              />
            )}
            {all.length > 8 && (
              <TextInput
                size="xs"
                w={200}
                leftSection={<IconSearch size={14} />}
                placeholder="Name, Anbieter, Referenz"
                aria-label="Verträge und Lizenzen durchsuchen"
                value={search}
                onChange={(e) => setSearch(e.currentTarget.value)}
              />
            )}
            {archived.length > 0 && (
              <Switch
                size="xs"
                label="Archiv zeigen"
                checked={showArchived}
                onChange={(e) => setShowArchived(e.currentTarget.checked)}
              />
            )}
            {data && (
              <Menu position="bottom-end" withinPortal>
                <Menu.Target>
                  <Button
                    size="xs"
                    variant="default"
                    leftSection={<IconDownload size={14} />}
                    disabled={list.length === 0}
                  >
                    CSV-Export
                  </Button>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Label>Für Excel exportieren</Menu.Label>
                  <Menu.Item leftSection={<IconUserCheck size={14} />} onClick={() => exportCsv('customer')}>
                    Für den Kunden (wie im Portal)
                  </Menu.Item>
                  <Menu.Item leftSection={<IconShieldCheck size={14} />} onClick={() => exportCsv('internal')}>
                    Intern (alle Angaben)
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            )}
            {data && (
              <Button
                size="xs"
                variant="subtle"
                color="gray"
                leftSection={<IconHistory size={14} />}
                onClick={() => {
                  setLogItem(null);
                  setLogOpen(true);
                }}
              >
                Protokoll
              </Button>
            )}
            <Button size="xs" leftSection={<IconPlus size={14} />} onClick={openAdd}>
              Hinzufügen
            </Button>
          </Group>
        </Group>

        {isLoading ? (
          <Center py="xl">
            <Loader size="sm" />
          </Center>
        ) : list.length === 0 ? (
          <InfoEmpty icon={<IconCalendarDue size={20} />} action={empty.action}>
            {empty.text}
          </InfoEmpty>
        ) : (
          list.map((d, index) => {
            // Ohne Laufzeitende (Laufzeit angefragt, Managed Service) gibt es keine Frist und keine Ampel.
            const fixed = d.termType === 'FIXED' ? d : null;
            const pending = d.termType === 'PENDING';
            const stripeColor = !d.isActive
              ? 'gray'
              : fixed
                ? DEADLINE_STATUS_META[fixed.status].color
                : pending
                  ? 'yellow'
                  : 'teal';
            const groupHeading = !fixed && (index === 0 || list[index - 1].termType !== d.termType);
            const own = DEADLINE_OWNERSHIP_LABELS[d.ownership];
            const CategoryIcon = CATEGORY_ICONS[d.category] ?? IconCalendarDue;
            const proofCount = d.proofs?.length ?? 0;
            const proofsInPortal = d.proofs?.filter((p) => p.visibleToCustomer).length ?? 0;
            const details = [
              d.provider,
              d.quantity != null ? `${d.quantity} Stk.` : null,
              d.reference ? `Ref. ${d.reference}` : null,
              d.termType === 'SERVICE_CONTRACT'
                ? null
                : d.autoRenew
                  ? `Auto-Verlängerung${d.renewalMonths ? ` ${d.renewalMonths} Mon.` : ''}`
                  : fixed?.status === 'ENDING'
                    ? null
                    : 'verlängert sich nicht von selbst',
              d.termType === 'SERVICE_CONTRACT' ? null : noticeLabel(d.noticeDays, d.noticeAnchor),
              fixed?.actionIsCancellation ? `Laufzeit bis ${formatIsoDateDe(fixed.termEndDate)}` : null,
            ]
              .filter(Boolean)
              .join(' · ');
            return (
              <Fragment key={d.id}>
                {groupHeading && (
                  <Group
                    gap={6}
                    px="md"
                    py={6}
                    wrap="wrap"
                    style={{ borderTop: '1px solid var(--mantine-color-default-border)', backgroundColor: 'var(--mantine-color-default-hover)' }}
                  >
                    {pending ? (
                      <IconHourglass size={13} color="var(--mantine-color-dimmed)" />
                    ) : (
                      <IconShieldCheck size={13} color="var(--mantine-color-dimmed)" />
                    )}
                    <Text size="xs" fw={600} c="dimmed">
                      {pending ? 'Laufzeit angefragt' : 'Managed Services'}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {pending
                        ? 'Laufzeitende folgt — im Portal mit Hinweis, noch ohne Frist'
                        : 'laufen mit dem FixByte-Servicevertrag — ohne Frist, Ampel und Erinnerung'}
                    </Text>
                  </Group>
                )}
                <div
                  className={d.isActive ? classes.row : `${classes.row} ${classes.muted}`}
                  // Farbstreifen links = Status; so erfasst man die Liste, ohne jede Zeile zu lesen.
                  style={{ boxShadow: `inset 3px 0 0 var(--mantine-color-${stripeColor}-6)` }}
                >
                  <Group wrap="nowrap" align="flex-start" gap="sm">
                    <Tooltip
                      label={`${d.ownership === 'FIXBYTE' ? 'FixByte stellt bereit' : 'Kunde ist Inhaber · FixByte verwaltet'} · ${DEADLINE_CATEGORY_LABELS[d.category]}`}
                    >
                      <ThemeIcon size={32} radius="md" variant="light" color={own.color} style={{ flexShrink: 0 }}>
                        <CategoryIcon size={17} />
                      </ThemeIcon>
                    </Tooltip>
                    {/* Breit: Frist als rechte Spalte. Schmal (Handy): Frist als Zeile unter dem Namen. */}
                    <div className={classes.deadlineMain}>
                      <div style={{ minWidth: 0 }}>
                        <Group gap={6} wrap="wrap">
                          <Text size="sm" fw={600}>
                            {d.name}
                          </Text>
                          <Badge size="xs" variant="outline" color="gray">
                            {DEADLINE_CATEGORY_LABELS[d.category]}
                          </Badge>
                          <Badge
                            size="xs"
                            variant="light"
                            color={own.color}
                            leftSection={d.ownership === 'FIXBYTE' ? <IconBuildingStore size={10} /> : <IconUserCheck size={10} />}
                            style={{ textTransform: 'none' }}
                          >
                            {d.ownership === 'FIXBYTE' ? 'FixByte stellt bereit' : 'Kunde · FixByte verwaltet'}
                          </Badge>
                          {!d.isActive && (
                            <Badge size="xs" variant="light" color="gray">
                              archiviert
                            </Badge>
                          )}
                          {!d.visibleInPortal && (
                            <Tooltip label="Im Kundenportal ausgeblendet">
                              <Badge size="xs" variant="light" color="gray" leftSection={<IconEyeOff size={10} />}>
                                intern
                              </Badge>
                            </Tooltip>
                          )}
                          {d.createdByCustomer && (
                            <Tooltip label="Vom Kunden selbst im Portal erfasst (Eigen-Tracking) — kein FixByte-Handlungsbedarf">
                              <Badge size="xs" variant="light" color="cyan" style={{ textTransform: 'none' }}>
                                vom Kunden
                              </Badge>
                            </Tooltip>
                          )}
                          {proofCount > 0 && (
                          <Tooltip label={`${proofCount} ${proofCount === 1 ? 'Beleg' : 'Belege'} · ${proofsInPortal} im Kundenportal`}>
                            <Badge
                              size="xs"
                              variant="light"
                              color="gray"
                              leftSection={<IconPaperclip size={10} />}
                              style={{ cursor: 'pointer' }}
                              onClick={() => {
                                setForm(toForm(d));
                                setEditItem(d);
                              }}
                            >
                              {proofCount}
                            </Badge>
                          </Tooltip>
                        )}
                        {d.reminderTicketNumber && (
                            <Tooltip label="Automatisch erzeugtes Fristen-Ticket (30 Tage vor Ablauf)">
                              <Badge size="xs" variant="outline" color="grape" style={{ textTransform: 'none' }}>
                                ⏰ {d.reminderTicketNumber}
                              </Badge>
                            </Tooltip>
                          )}
                          {d.pendingRequest && (
                            <Tooltip
                              label={`Kunde hat ${DEADLINE_REQUEST_KIND_LABELS[d.pendingRequest.kind as keyof typeof DEADLINE_REQUEST_KIND_LABELS] ?? 'eine Anfrage'} angefragt${d.pendingRequest.at ? ` (${new Date(d.pendingRequest.at).toLocaleDateString('de-DE')})` : ''} – nach Bearbeitung hier als erledigt markieren`}
                              multiline
                              w={260}
                            >
                              <Badge
                                size="xs"
                                variant="filled"
                                color="grape"
                                style={{ textTransform: 'none', cursor: 'pointer' }}
                                onClick={() => resolveMutation.mutate(d.id)}
                              >
                                Kunde: {DEADLINE_REQUEST_KIND_LABELS[d.pendingRequest.kind as keyof typeof DEADLINE_REQUEST_KIND_LABELS] ?? 'Anfrage'}
                                {d.pendingRequest.ticketNumber ? ` #${d.pendingRequest.ticketNumber}` : ''} · erledigt ✓
                              </Badge>
                            </Tooltip>
                          )}
                        </Group>
                        {details && (
                          <Text size="xs" c="dimmed" mt={4}>
                            {details}
                          </Text>
                        )}
                        {d.internalNote && (
                          <Text size="xs" c="dimmed" fs="italic" lineClamp={2} mt={2}>
                            {d.internalNote}
                          </Text>
                        )}
                      </div>

                      <div className={classes.deadlineWhen}>
                        {fixed ? (
                          <>
                            {d.isActive && fixed.status !== 'OK' && (
                              <Badge size="xs" variant="light" color={stripeColor} mb={4}>
                                {DEADLINE_STATUS_META[fixed.status].label}
                              </Badge>
                            )}
                            <Text size="xs" c="dimmed" lh={1.3}>
                              {fixed.actionIsCancellation ? 'Kündigung bis' : fixed.autoRenew ? 'Verlängert sich am' : 'Läuft ab am'}
                            </Text>
                            <Text size="sm" fw={600} lh={1.3} c={d.isActive ? stripeColor : 'dimmed'}>
                              {formatIsoDateDe(fixed.actionDate)}
                            </Text>
                            <Text size="xs" c="dimmed" lh={1.3}>
                              {formatDaysLeft(fixed.daysLeft)}
                            </Text>
                          </>
                        ) : (
                          <>
                            <Text size="xs" c="dimmed" lh={1.3}>
                              Laufzeit
                            </Text>
                            <Text size="sm" fw={600} lh={1.3} c={d.isActive ? (pending ? 'yellow.8' : 'teal') : 'dimmed'}>
                              {pending ? 'angefragt' : 'Servicevertrag'}
                            </Text>
                            <Text size="xs" c="dimmed" lh={1.3}>
                              {pending ? 'wird nachgetragen' : 'keine eigene Frist'}
                            </Text>
                          </>
                        )}
                      </div>
                    </div>

                    <Group gap={2} wrap="nowrap" style={{ flexShrink: 0 }}>
                      <Tooltip label={d.visibleInPortal ? 'Im Portal sichtbar — ausblenden' : 'Im Portal ausgeblendet — anzeigen'}>
                        <ActionIcon
                          variant="subtle"
                          size="sm"
                          color="gray"
                          aria-label={d.visibleInPortal ? `${d.name} im Portal ausblenden` : `${d.name} im Portal anzeigen`}
                          onClick={() => patchMutation.mutate({ id: d.id, data: { visibleInPortal: !d.visibleInPortal } })}
                        >
                          {d.visibleInPortal ? <IconEye size={15} /> : <IconEyeOff size={15} />}
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label="Bearbeiten">
                        <ActionIcon
                          variant="subtle"
                          size="sm"
                          color="gray"
                          aria-label={`${d.name} bearbeiten`}
                          onClick={() => {
                            setForm(toForm(d));
                            setEditItem(d);
                          }}
                        >
                          <IconPencil size={15} />
                        </ActionIcon>
                      </Tooltip>
                      <Menu position="bottom-end" shadow="md" zIndex={1100} withinPortal>
                        <Menu.Target>
                          <ActionIcon variant="subtle" size="sm" color="gray" aria-label={`Weitere Aktionen für ${d.name}`}>
                            <IconDotsVertical size={15} />
                          </ActionIcon>
                        </Menu.Target>
                        <Menu.Dropdown>
                          <Menu.Item
                            leftSection={<IconHistory size={14} />}
                            onClick={() => {
                              setLogItem({ id: d.id, name: d.name });
                              setLogOpen(true);
                            }}
                          >
                            Verlauf
                          </Menu.Item>
                          <Menu.Item
                            leftSection={d.isActive ? <IconArchive size={14} /> : <IconArchiveOff size={14} />}
                            onClick={() => patchMutation.mutate({ id: d.id, data: { isActive: !d.isActive } })}
                          >
                            {d.isActive ? 'Archivieren (gekündigt/beendet)' : 'Reaktivieren'}
                          </Menu.Item>
                          <Menu.Divider />
                          <Menu.Item color="red" leftSection={<IconTrash size={14} />} onClick={() => confirmDelete(d)}>
                            Löschen
                          </Menu.Item>
                        </Menu.Dropdown>
                      </Menu>
                    </Group>
                  </Group>
                </div>
              </Fragment>
            );
          })
        )}
      </Paper>

      {data && (
        <DeadlineChangeLogModal
          opened={logOpen}
          onClose={() => setLogOpen(false)}
          customerId={data.customer.id}
          deadline={logItem}
        />
      )}

      <Modal
        opened={addOpen || editItem !== null}
        onClose={closeModal}
        title={editItem ? `${editItem.name} bearbeiten` : 'Vertrag / Lizenz hinzufügen'}
        zIndex={800}
        size="lg"
        centered
      >
        <Stack
          gap="sm"
          onPaste={(e) => {
            const images = imagesFromClipboard(e);
            if (images.length === 0) return;
            e.preventDefault();
            setRedactQueue((queue) => [...queue, ...images.map((file) => ({ file }))]);
          }}
        >
          {/* Prefill nur bei Neuanlage: beim Bearbeiten würde ein Dokument die
              bereits gepflegten Werte überschreiben. */}
          {!editItem && (
            <Paper withBorder radius="md" p="sm" bg="var(--mantine-color-default-hover)">
              <Group justify="space-between" wrap="nowrap" gap="sm">
                <div style={{ minWidth: 0 }}>
                  <Text size="sm" fw={600}>
                    Aus Dokument übernehmen
                  </Text>
                  <Text size="xs" c="dimmed">
                    Screenshot oder PDF der Lizenz-/Vertragsunterlage hochladen — KIAN füllt die
                    Felder vor. Nichts wird gespeichert, du prüfst alles vor dem Speichern.
                  </Text>
                </div>
                <FileButton
                  onChange={handlePrefillFile}
                  accept="application/pdf,image/png,image/jpeg,image/webp"
                >
                  {(props) => (
                    <Button
                      {...props}
                      variant="light"
                      size="compact-sm"
                      loading={prefillState === 'loading'}
                      leftSection={<IconSparkles size={14} />}
                      style={{ flexShrink: 0 }}
                    >
                      Datei wählen
                    </Button>
                  )}
                </FileButton>
              </Group>
              {prefillNote && (
                <Alert
                  mt="xs"
                  py={6}
                  color={prefillState === 'error' ? 'red' : 'teal'}
                  variant="light"
                  icon={<IconInfoCircle size={14} />}
                >
                  <Text size="xs">{prefillNote}</Text>
                </Alert>
              )}
            </Paper>
          )}
          <div>
            <Text size="sm" fw={500} mb={4}>Laufzeit</Text>
            <SegmentedControl
              fullWidth
              size="xs"
              value={form.termType}
              onChange={(v) =>
                setForm((f) => ({
                  ...f,
                  termType: v as CustomerDeadlineTermType,
                  ...(v === 'SERVICE_CONTRACT' ? { ownership: 'FIXBYTE' as const } : {}),
                }))
              }
              data={[
                { value: 'FIXED', label: 'Eigene Laufzeit' },
                { value: 'PENDING', label: 'Laufzeit angefragt' },
                { value: 'SERVICE_CONTRACT', label: 'Läuft mit FixByte-Servicevertrag' },
              ]}
            />
            <Text size="xs" c="dimmed" mt={4}>
              {form.termType === 'SERVICE_CONTRACT'
                ? 'Managed Service (z. B. Virenschutz, Backup, MDM): keine eigene Laufzeit oder Kündigungsfrist beim Hersteller – im Portal ohne Frist, Ampel und Erinnerung.'
                : form.termType === 'PENDING'
                  ? 'Laufzeitende noch unbekannt und beim Anbieter angefragt – im Portal steht „Laufzeit angefragt, wird in Kürze hinterlegt". Verlängerung und Kündigungsfrist schon eintragen, wenn bekannt.'
                  : 'Mit Laufzeitende – daraus ergeben sich Handlungsfrist, Ampel und Erinnerungen.'}
            </Text>
          </div>
          <div>
            <Text size="sm" fw={500} mb={4}>Vertragspartner / Inhaber</Text>
            <SegmentedControl
              fullWidth
              size="xs"
              disabled={form.termType === 'SERVICE_CONTRACT'}
              value={form.ownership}
              onChange={(v) => setForm((f) => ({ ...f, ownership: v as CustomerDeadlineOwnership }))}
              data={[
                { value: 'FIXBYTE', label: 'FixByte stellt bereit (eigener Name)' },
                { value: 'CUSTOMER_MANAGED', label: 'Kunde ist Inhaber · FixByte verwaltet' },
              ]}
            />
            <Text size="xs" c="dimmed" mt={4}>
              {form.termType === 'SERVICE_CONTRACT'
                ? 'Managed Services bezieht der Kunde über FixByte – FixByte stellt bereit.'
                : `${DEADLINE_OWNERSHIP_LABELS[form.ownership].long}.`}
            </Text>
          </div>
          <Group gap="sm" grow align="flex-start">
            <Select
              label="Art"
              data={DEADLINE_CATEGORY_ORDER.map((c) => ({ value: c, label: DEADLINE_CATEGORY_LABELS[c] }))}
              value={form.category}
              onChange={(v) => v && setForm((f) => ({ ...f, category: v as CustomerDeadlineCategory }))}
              allowDeselect={false}
              comboboxProps={{ zIndex: 900 }}
            />
            <TextInput
              label="Bezeichnung"
              required
              placeholder="z. B. Microsoft 365 Business Premium, Domain kunde.de"
              value={form.name}
              onChange={(e) => { const __value = e.currentTarget.value; setForm((f) => ({ ...f, name: __value })); }}
            />
          </Group>
          <Group gap="sm" grow align="flex-start">
            <TextInput
              label="Anbieter / Hersteller"
              placeholder="Microsoft, IONOS, Hetzner …"
              value={form.provider}
              onChange={(e) => { const __value = e.currentTarget.value; setForm((f) => ({ ...f, provider: __value })); }}
            />
            <TextInput
              label={<LabelWithHint label="Referenz" hint="Vertrags-/Kundennummer beim Anbieter – keine Lizenzschlüssel oder Passwörter." />}
              placeholder="Vertrags-/Kundennummer"
              value={form.reference}
              onChange={(e) => { const __value = e.currentTarget.value; setForm((f) => ({ ...f, reference: __value })); }}
            />
            <NumberInput
              label="Menge"
              placeholder="Seats / Stück"
              min={0}
              value={form.quantity}
              onChange={(v) => setForm((f) => ({ ...f, quantity: v === '' ? '' : Number(v) }))}
            />
          </Group>
          <Group gap="sm" grow align="flex-start">
            <TextInput
              label="Laufzeitbeginn"
              type="date"
              value={form.startDate}
              onChange={(e) => { const __value = e.currentTarget.value; setForm((f) => ({ ...f, startDate: __value })); }}
            />
            {form.termType === 'FIXED' && (
              <TextInput
                label="Laufzeitende / Frist"
                type="date"
                required
                value={form.endDate}
                onChange={(e) => { const __value = e.currentTarget.value; setForm((f) => ({ ...f, endDate: __value })); }}
              />
            )}
          </Group>
          {/* Managed Services enden mit dem Servicevertrag; bei „angefragt" sind die Bedingungen oft schon bekannt. */}
          {form.termType !== 'SERVICE_CONTRACT' && (
            <>
              <Checkbox
                label={<LabelWithHint label="Verlängert sich automatisch" hint="Dann zählt die Kündigungsfrist als Handlungsfrist im Portal." />}
                checked={form.autoRenew}
                onChange={(e) => { const __checked = e.currentTarget.checked; setForm((f) => ({ ...f, autoRenew: __checked })); }}
              />
              <Group gap="sm" grow align="flex-start">
                <NumberInput
                  label="Verlängerung (Monate)"
                  placeholder="z. B. 12"
                  min={0}
                  disabled={!form.autoRenew}
                  value={form.renewalMonths}
                  onChange={(v) => setForm((f) => ({ ...f, renewalMonths: v === '' ? '' : Number(v) }))}
                />
                <NumberInput
                  label="Kündigungsfrist (Tage vor Ende)"
                  placeholder="z. B. 30 / 90"
                  min={0}
                  value={form.noticeDays}
                  onChange={(v) => setForm((f) => ({ ...f, noticeDays: v === '' ? '' : Number(v) }))}
                />
              </Group>
            </>
          )}
          <Textarea
            label={<LabelWithHint label="Hinweis für den Kunden" hint="Wird im Kundenportal angezeigt." />}
            placeholder="z. B. Verlängerung erfolgt automatisch über FixByte; Änderungswünsche bitte bis 4 Wochen vorher."
            value={form.customerNote}
            onChange={(e) => { const __value = e.currentTarget.value; setForm((f) => ({ ...f, customerNote: __value })); }}
            autosize
            minRows={2}
            maxRows={5}
          />
          <Textarea
            label={<LabelWithHint label="Interne Notiz" hint="Nur für Techniker sichtbar." />}
            value={form.internalNote}
            onChange={(e) => { const __value = e.currentTarget.value; setForm((f) => ({ ...f, internalNote: __value })); }}
            autosize
            minRows={2}
            maxRows={5}
          />
          <DeadlineProofsField
            deadlineId={editItem?.id ?? null}
            proofs={editItem ? (all.find((x) => x.id === editItem.id)?.proofs ?? editItem.proofs ?? []) : []}
            pending={pendingProofs}
            onPendingChange={setPendingProofs}
            onAddImages={(files) => setRedactQueue((queue) => [...queue, ...files.map((file) => ({ file }))])}
            onRedact={(item) => setRedactQueue((queue) => [...queue, { file: item.file, replaceKey: item.key }])}
            onChanged={invalidate}
          />
          <Checkbox
            label="Im Kundenportal anzeigen"
            checked={form.visibleInPortal}
            onChange={(e) => { const __checked = e.currentTarget.checked; setForm((f) => ({ ...f, visibleInPortal: __checked })); }}
          />
          <Group justify="flex-end" gap="sm">
            <Button variant="subtle" onClick={closeModal}>Abbrechen</Button>
            <Button
              disabled={form.name.trim().length < 2 || (form.termType === 'FIXED' && !form.endDate)}
              loading={saveMutation.isPending}
              onClick={() => saveMutation.mutate()}
            >
              Speichern
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={deleteItem !== null}
        onClose={() => setDeleteItem(null)}
        title="Eintrag löschen?"
        zIndex={900}
        size="sm"
        centered
      >
        <Stack gap="sm">
          <Text size="sm">
            „{deleteItem?.name}" wird endgültig gelöscht. Zum Aufbewahren in der Historie lieber archivieren.
          </Text>
          <Group justify="flex-end" gap="sm">
            <Button variant="subtle" onClick={() => setDeleteItem(null)}>Abbrechen</Button>
            <Button
              color="red"
              loading={deleteMutation.isPending}
              onClick={() => deleteItem && deleteMutation.mutate(deleteItem.id)}
            >
              Löschen
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Neben, nicht in dem Bearbeiten-Dialog: sonst schlösse Esc über das React-Bubbling beide. */}
      <ProofRedactModal
        target={redactQueue[0] ?? null}
        remaining={Math.max(0, redactQueue.length - 1)}
        onApply={(file) => {
          const target = redactQueue[0];
          if (target?.replaceKey) {
            // Das unbearbeitete Bild fliegt raus — hochgeladen wird nur die neue Fassung.
            const previous = pendingProofs.find((p) => p.key === target.replaceKey);
            if (previous) URL.revokeObjectURL(previous.previewUrl);
            const previewUrl = URL.createObjectURL(file);
            setPendingProofs((prev) => prev.map((p) => (p.key === target.replaceKey ? { ...p, file, previewUrl } : p)));
          } else {
            const added = toPendingProofs([file]);
            setPendingProofs((prev) => [...prev, ...added]);
          }
          setRedactQueue((queue) => queue.slice(1));
        }}
        onDiscard={() => setRedactQueue((queue) => queue.slice(1))}
      />
    </div>
  );
}
